'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import gsap from 'gsap';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { cn } from '@/components/ui/utils';
import { stripHtml, getImageUrlForVariant } from '@/lib/homepage-mappers';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { SustainabilityLeftRightSection } from '@/types/sustainability';

type StandItem = {
  id: string;
  title: string;
  description: string;
};

type StandCard = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  cta: string;
  imageSrc: string;
  reverse?: boolean;
  items: StandItem[];
};

const AUTO_ADVANCE_MS = 8000;

const STAND_CARDS: StandCard[] = [
  {
    id: 'environment',
    title: 'Environment:',
    subtitle: 'Decarbonising the Value Chain',
    description:
      'We promote resource efficiency and carbon mitigation through advanced thermal systems and circular economy practices to reduce our industrial footprint.',
    cta: 'Environmental Performance',
    imageSrc: '/images/stand_img1.jpg',
    items: [
      {
        id: 'renewable',
        title: 'Renewable Transition',
        description:
          '33.6% of our total energy is now derived from renewable sources, supported by WT 1,960 GJ generated from Waste Heat Recovery Systems.',
      },
      {
        id: 'water',
        title: 'Water Stewardship',
        description:
          'Our plants continuously optimize water usage using recirculation systems and responsible sourcing to protect community water resources.',
      },
      {
        id: 'circular',
        title: 'Circular Economy',
        description:
          'We use alternative fuels and industrial by-products to improve resource efficiency and reduce raw material dependency across operations.',
      },
    ],
  },
  {
    id: 'social',
    title: 'Social:',
    subtitle: 'Empowering People and Communities',
    description:
      'Our social strategy focuses on a Zero Harm workplace culture and the holistic development of the communities residing near our manufacturing sites.',
    cta: 'Social Impact',
    imageSrc: '/images/stand_img2.jpg',
    reverse: true,
    items: [
      {
        id: 'safety',
        title: 'Safety Record',
        description:
          'Maintained zero fatalities for all employees and workers, reducing the LTIFR for employees from 1.66 to 0.88 per million hours worked.',
      },
      {
        id: 'community',
        title: 'Community Impact',
        description:
          'Through focused development initiatives, we support health, education, and livelihood opportunities in nearby communities.',
      },
      {
        id: 'culture',
        title: 'Culture & Growth',
        description:
          'Our people programs promote inclusion, learning, and leadership development to build a resilient and future-ready workforce.',
      },
    ],
  },
  {
    id: 'governance',
    title: 'Governance:',
    subtitle: 'Leading with Integrity and Transparency',
    description:
      'We uphold the highest standards of corporate ethics through board-level supervision and a rigorous risk management framework that spans our entire supply chain.',
    cta: 'Governance Excellence',
    imageSrc: '/images/stand_img3.jpg',
    items: [
      {
        id: 'ethical',
        title: 'Ethical Compliance',
        description:
          'Achieved zero cases of anti-bribery, corruption, or data breaches, with a whistleblower policy covering zero retaliation cases.',
      },
      {
        id: 'supply',
        title: 'Supply Chain Responsibility',
        description:
          'Supplier governance standards and due diligence processes are embedded to ensure responsible sourcing and compliance at scale.',
      },
      {
        id: 'training',
        title: 'Supervision & Training',
        description:
          'Continuous ethics and compliance training equips teams with clear accountability and governance best practices.',
      },
    ],
  },
];

function StandAccordion({ items }: { items: StandItem[] }) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = React.useState(items[0]?.id ?? '');
  const [isInView, setIsInView] = React.useState(false);
  const [viewSession, setViewSession] = React.useState(0);
  const progressRefs = React.useRef<Record<string, HTMLSpanElement | null>>({});
  const progressTween = React.useRef<gsap.core.Tween | null>(null);

  React.useEffect(() => {
    setActiveId(items[0]?.id ?? '');
  }, [items]);

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.35 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isInView) {
      progressTween.current?.kill();
      for (const item of items) {
        const progressEl = progressRefs.current[item.id];
        if (progressEl) {
          gsap.killTweensOf(progressEl);
          gsap.set(progressEl, { width: '0%' });
        }
      }
      return;
    }

    const firstId = items[0]?.id ?? '';
    if (firstId) setActiveId(firstId);
    setViewSession((session) => session + 1);
  }, [isInView, items]);

  const goToNext = React.useCallback(() => {
    const currentIndex = items.findIndex((x) => x.id === activeId);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0;
    const nextId = items[nextIndex]?.id;
    if (nextId) setActiveId(nextId);
  }, [activeId, items]);

  React.useEffect(() => {
    if (!isInView || items.length <= 1) {
      progressTween.current?.kill();
      return;
    }

    for (const item of items) {
      const isActive = item.id === activeId;
      const progressEl = progressRefs.current[item.id];

      if (progressEl && !isActive) {
        gsap.killTweensOf(progressEl);
        gsap.set(progressEl, { width: '0%' });
      }
    }

    const activeProgressEl = progressRefs.current[activeId];
    if (activeProgressEl) {
      progressTween.current?.kill();
      gsap.set(activeProgressEl, { width: '0%' });
      progressTween.current = gsap.to(activeProgressEl, {
        width: '100%',
        duration: AUTO_ADVANCE_MS / 1000,
        ease: 'none',
        onComplete: goToNext,
      });
    }

    return () => {
      progressTween.current?.kill();
    };
  }, [activeId, goToNext, isInView, items, viewSession]);

  return (
    <div ref={rootRef} className="divide-y divide-[#D8D8D8] border-y border-[#D8D8D8]">
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <div key={item.id} className="relative">
            <div className="pointer-events-none absolute left-0 right-0 top-0 h-[2px] overflow-hidden bg-[#ECECEC]">
              <span
                ref={(el) => {
                  progressRefs.current[item.id] = el;
                }}
                className="absolute inset-y-0 left-0 w-0 bg-[#E31E24]"
              />
            </div>

            <button
              type="button"
              onClick={() => setActiveId(item.id)}
              className={cn(
                'flex w-full items-center justify-between gap-4 px-0 py-3.5 text-left transition-colors sm:py-4',
                isActive ? 'text-[#151515]' : 'text-[#242424]',
              )}
            >
              <span className="text-base font-medium leading-tight sm:text-[18px]">
                {item.title}
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-[#2A2A2A] transition-transform duration-300',
                  isActive && 'rotate-180',
                )}
              />
            </button>

            <div className={cn(!isActive && 'hidden')}>
              <p className="pb-4 pr-2 text-[12px] leading-[1.55] text-[#515151] sm:pr-6">
                {item.description}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

type StandContentCard = {
  id: string;
  title: string;
  subtitle: string;
  body: string;
  items: StandItem[];
  ctaText?: string | null;
  ctaHref?: string | null;
  ctaAriaLabel?: string | null;
  ctaLinkProps?: ReturnType<typeof getCmsButtonLinkProps>;
};

function StandContent({ card }: { card: StandContentCard }) {
  return (
    <div className="h-full bg-[#F2F2F1] p-5 sm:p-8 md:p-10 lg:p-12">
      <h3 className="text-[1.75rem] font-bold leading-tight text-[#111111] sm:text-3xl md:text-4xl">
        <span className="block">{card.title}</span>
        <span className="mt-1 block font-medium">{card.subtitle}</span>
      </h3>
      <p className="mt-3 max-w-[600px] text-[12px] leading-relaxed text-[#4A4A4A] sm:mt-4">
        {card.body}
      </p>
      {card.items.length > 0 ? (
        <div className="mt-5 sm:mt-6">
          <StandAccordion items={card.items} />
        </div>
      ) : null}
      {card.ctaText ? (
        <SwapTextButton
          text={card.ctaText}
          hoverText={card.ctaText}
          href={card.ctaHref ?? undefined}
          aria-label={card.ctaAriaLabel ?? undefined}
          {...card.ctaLinkProps}
          variant="outline"
          className="mt-5 min-h-[38px] px-3 py-2 text-[11px] shadow-none"
        />
      ) : null}
    </div>
  );
}

function StandImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative h-full min-h-[300px] bg-neutral-100 sm:min-h-[360px] md:min-h-[430px] lg:min-h-[500px]">
      <CmsImage
        src={src}
        alt={alt}
        fill
        className="object-cover"
        sizes="(min-width: 1024px) 50vw, 100vw"
        fallbackSrc={MEDIA_PLACEHOLDER}
      />
    </div>
  );
}

interface SustainabilityStandSectionProps {
  sections?: SustainabilityLeftRightSection[];
  locale?: string;
}

type StandCardLike = {
  id: number | string;
  sectionId?: string;
  title: string;
  subtitle: string;
  body: string;
  imageSrc: string;
  imageOnLeft: boolean;
  items: StandItem[];
  ctaText?: string | null;
  ctaHref?: string | null;
  ctaAriaLabel?: string | null;
  ctaLinkProps?: ReturnType<typeof getCmsButtonLinkProps>;
};

const FALLBACK_IMAGE = '/images/sustain.png';

export default function SustainabilityStandSection({
  sections,
  locale = 'en',
}: SustainabilityStandSectionProps) {
  const cards: StandCardLike[] =
    sections && sections.length > 0
      ? sections.map((s) => {
          const slug = s.sectionId?.replace(/^[#/]+/, '').replace(/^sustainability\//, '') || '';
          return {
            id: s.id,
            sectionId: slug,
            title: s.mainHeading,
            subtitle: s.subHeading,
            body: stripHtml(s.description),
            imageSrc: getImageUrlForVariant(s.media as never, 'large') || FALLBACK_IMAGE,
            imageOnLeft: s.Alignment === 'Left Image Right Text',
            items: (s.accordionItem ?? []).map((item) => ({
              id: String(item.id),
              title: item.heading,
              description: stripHtml(item.content) || '',
            })),
            ctaText: s.button?.[0]?.buttonText ?? null,
            ctaHref: slug ? `/${locale}/sustainability/${slug}` : null,
            ctaAriaLabel: s.button?.[0]?.ariaLabel ?? null,
            ctaLinkProps: getCmsButtonLinkProps(s.button?.[0]),
          };
        })
      : STAND_CARDS.map((c) => ({
          id: c.id,
          sectionId: c.id,
          title: c.title,
          subtitle: c.subtitle,
          body: c.description,
          imageSrc: c.imageSrc,
          imageOnLeft: !c.reverse,
          items: c.items,
          ctaText: c.cta,
          ctaHref: null,
          ctaAriaLabel: null,
          ctaLinkProps: {},
        }));

  return (
    <section className="relative z-10 bg-black">
      <div className="w-full">
        <div className="overflow-hidden rounded-sm">
          {cards.map((card) => (
            <article
              key={card.id}
              id={card.sectionId || undefined}
              className="grid grid-cols-1 lg:grid-cols-2"
            >
              <div className={cn('order-2', card.imageOnLeft ? 'lg:order-1' : 'lg:order-2')}>
                <StandContent
                  card={{
                    id: String(card.id),
                    title: card.title,
                    subtitle: card.subtitle,
                    body: card.body,
                    items: card.items,
                    ctaText: card.ctaText,
                    ctaHref: card.ctaHref,
                    ctaAriaLabel: card.ctaAriaLabel,
                    ctaLinkProps: card.ctaLinkProps,
                  }}
                />
              </div>
              <div className={cn('order-1', card.imageOnLeft ? 'lg:order-2' : 'lg:order-1')}>
                <StandImage src={card.imageSrc} alt={`${card.title} ${card.subtitle}`} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
